import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSportContent } from '../hooks/useSportContent';
import { useUserProfile } from '../hooks/useUserProfile';
import { SportAssessment } from '../content';
import SkillProgressTab from '../components/SkillProgressTab';

interface AssessmentResult {
  assessmentId: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  completedAt: string;
  answers: { questionId: string; selectedAnswer: number; isCorrect: boolean }[];
}

const SkillAssessmentScreen: React.FC = () => {
  const { currentSport } = useTheme();
  const { user } = useAuth();
  const { content, loading, error } = useSportContent(currentSport.id);
  const { addAssessmentResult } = useUserProfile();
  const [selectedAssessment, setSelectedAssessment] = useState<SportAssessment | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: string]: number }>({});
  const [showResults, setShowResults] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState<AssessmentResult[]>([]);
  const [activeTab, setActiveTab] = useState<'assessments' | 'progress'>('assessments');

  useEffect(() => {
    if (user && content && content.assessments) {
      // Load saved assessment results from localStorage
      const savedResults = localStorage.getItem(`assessment-results-${user.id}-${currentSport.id}`);
      if (savedResults) {
        setAssessmentResults(JSON.parse(savedResults));
      }
    }
  }, [user, currentSport.id, content]);

  const startAssessment = (assessment: SportAssessment) => {
    setSelectedAssessment(assessment);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
  };

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const nextQuestion = () => {
    if (selectedAssessment && currentQuestionIndex < selectedAssessment.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      finishAssessment();
    }
  };

  const finishAssessment = () => {
    if (!selectedAssessment) return;

    const results = selectedAssessment.questions.map(question => ({
      questionId: question.id,
      selectedAnswer: answers[question.id] ?? -1,
      isCorrect: answers[question.id] === question.correctAnswer
    }));

    const score = results.filter(r => r.isCorrect).length;
    const totalQuestions = selectedAssessment.questions.length;
    const percentage = Math.round((score / totalQuestions) * 100);

    const assessmentResult: AssessmentResult = {
      assessmentId: selectedAssessment.id,
      score,
      totalQuestions,
      percentage,
      completedAt: new Date().toISOString(),
      answers: results
    };

    const newResults = [...assessmentResults, assessmentResult];
    setAssessmentResults(newResults);

    // Save to user profile using the new profile system
    if (user && selectedAssessment) {
      const profileResult = {
        id: `${selectedAssessment.id}-${Date.now()}`,
        sport: currentSport.id,
        assessmentType: selectedAssessment.title,
        score: assessmentResult.score,
        totalQuestions: assessmentResult.totalQuestions,
        percentage: assessmentResult.percentage,
        timestamp: assessmentResult.completedAt,
        answers: assessmentResult.answers.map(answer => {
          const question = selectedAssessment.questions.find(q => q.id === answer.questionId);
          return {
            question: question?.question || '',
            userAnswer: question?.options[answer.selectedAnswer] || '',
            correctAnswer: question?.options[question.correctAnswer] || '',
            isCorrect: answer.isCorrect
          };
        })
      };
      
      addAssessmentResult(profileResult);
    }

    // Also save to localStorage for backward compatibility
    if (user) {
      const storageKey = `assessment-results-${user.id}-${currentSport.id}`;
      console.log('Saving assessment results to localStorage with key:', storageKey);
      console.log('Results being saved:', newResults);
      localStorage.setItem(storageKey, JSON.stringify(newResults));
    }

    setShowResults(true);
  };

  const resetAssessment = () => {
    setSelectedAssessment(null);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
  };

  if (loading) {
    return (
      <div className="skill-assessment-screen">
        <div className="container">
          <div className="loading">Loading assessments...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="skill-assessment-screen">
        <div className="container">
          <div className="error">Error loading assessments: {error}</div>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('SkillAssessmentScreen - content:', content);
  console.log('SkillAssessmentScreen - currentSport:', currentSport);

  if (showResults && selectedAssessment) {
    const result = assessmentResults[assessmentResults.length - 1];
    return (
      <div className="skill-assessment-screen">
        <div className="container">
          <h1>Assessment Results</h1>
          <div className="assessment-results">
            <h2>{selectedAssessment.title}</h2>
            <div className="score-display">
              <div className="score-circle">
                <span className="score">{result.score}</span>
                <span className="total">/{result.totalQuestions}</span>
              </div>
              <div className="percentage">{result.percentage}%</div>
            </div>
            <div className="results-breakdown">
              {selectedAssessment.questions.map((question, index) => {
                const userAnswer = result.answers.find(a => a.questionId === question.id);
                const isCorrect = userAnswer?.isCorrect ?? false;
                return (
                  <div key={question.id} className={`question-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="question-number">Q{index + 1}</div>
                    <div className="question-text">{question.question}</div>
                    <div className="answer-feedback">
                      {isCorrect ? (
                        <span className="correct">✓ Correct</span>
                      ) : (
                        <div>
                          <span className="incorrect">✗ Incorrect</span>
                          <div className="explanation">{question.explanation}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="assessment-actions">
              <button onClick={resetAssessment} className="btn btn-primary">
                Take Another Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedAssessment) {
    const currentQuestion = selectedAssessment.questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === selectedAssessment.questions.length - 1;
    const hasAnswered = answers[currentQuestion.id] !== undefined;

    return (
      <div className="skill-assessment-screen">
        <div className="container">
          <div className="assessment-header">
            <h1>{selectedAssessment.title}</h1>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((currentQuestionIndex + 1) / selectedAssessment.questions.length) * 100}%` }}
              />
            </div>
            <div className="question-counter">
              Question {currentQuestionIndex + 1} of {selectedAssessment.questions.length}
            </div>
          </div>

          <div className="question-container">
            <h2 className="question-text">{currentQuestion.question}</h2>
            <div className="answer-options">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  className={`answer-option ${answers[currentQuestion.id] === index ? 'selected' : ''}`}
                  onClick={() => handleAnswerSelect(currentQuestion.id, index)}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="question-actions">
              <button
                onClick={nextQuestion}
                disabled={!hasAnswered}
                className="btn btn-primary"
              >
                {isLastQuestion ? 'Finish Assessment' : 'Next Question'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="skill-assessment-screen">
      <div className="container">
        <div className="assessment-header">
          <h1>{currentSport.name} Skill Assessment</h1>
          <p>Test your knowledge and track your skill progress</p>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'assessments' ? 'active' : ''}`}
            onClick={() => setActiveTab('assessments')}
          >
            📊 Assessments
          </button>
          <button
            className={`tab-button ${activeTab === 'progress' ? 'active' : ''}`}
            onClick={() => setActiveTab('progress')}
          >
            ✅ Skill Progress
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'assessments' && (
          <div className="tab-content">
            {user && assessmentResults.length > 0 && (
              <div className="previous-results">
                <h2>Your Previous Results</h2>
                <div className="results-grid">
                  {assessmentResults.map((result, index) => {
                    const assessment = content?.assessments?.find(a => a.id === result.assessmentId);
                    return (
                      <div key={index} className="result-card">
                        <h3>{assessment?.title || 'Unknown Assessment'}</h3>
                        <div className="result-score">
                          {result.score}/{result.totalQuestions} ({result.percentage}%)
                        </div>
                        <div className="result-date">
                          {new Date(result.completedAt).toLocaleDateString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="assessments-grid">
              {content?.assessments && content.assessments.length > 0 ? (
                content.assessments.map((assessment) => (
                  <div key={assessment.id} className="assessment-card">
                    <h3>{assessment.title}</h3>
                    <p>{assessment.description}</p>
                    <div className="assessment-meta">
                      <span className="category">{assessment.category}</span>
                      <span className="question-count">{assessment.questions.length} questions</span>
                    </div>
                    <button
                      onClick={() => startAssessment(assessment)}
                      className="btn btn-primary"
                    >
                      Start Assessment
                    </button>
                  </div>
                ))
              ) : (
                <div className="no-assessments">
                  <p>No assessments available for {currentSport.name}.</p>
                  <p>Content status: {content ? 'Loaded' : 'Not loaded'}</p>
                  <p>Assessments: {content?.assessments ? content.assessments.length : 'undefined'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="tab-content">
            <SkillProgressTab />
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillAssessmentScreen;
